import {ProductInterface} from "../interfaces/catalog/ProductInterface";

export type SortType = 'popularity' | 'sizes' | 'articul' | 'code' | 'priceasc' | 'pricedesc';

export const DEFAULT_SORT: SortType = 'sizes';

/**
 * Варианты сортировки (аналогично ProductListSort.vue основного сайта)
 */
export const SORT_VARIANTS: Record<SortType, string> = {
    popularity: 'Популярности',
    sizes: 'Размерности',
    articul: 'Артикулу',
    code: 'Коду',
    priceasc: 'Цене ↑',
    pricedesc: 'Цене ↓',
};

export const isSortType = (value: string | null | undefined): value is SortType =>
    !!value && Object.keys(SORT_VARIANTS).includes(value);

// Товары без индекса уходят в конец списка
const NO_INDEX = 10000000;

const getPrice = (product: ProductInterface) => (product.client_price || product.current_price)?.amount || 0;

const hasStock = (product: ProductInterface) => product.has_client_stock ?? product.has_stock ?? false;

/**
 * Сортирует продукты так, чтобы товары в наличии отображались первыми
 * @param products - массив продуктов для сортировки
 * @returns отсортированный массив продуктов
 */
export const sortProductsByStock = (products: ProductInterface[]): ProductInterface[] => {
    return [...products].sort((a, b) => {
        const aInStock = hasStock(a);
        const bInStock = hasStock(b);

        if (aInStock === bInStock) return 0;
        return aInStock ? -1 : 1;
    });
};

/**
 * Сортирует продукты по выбранному типу сортировки.
 * По умолчанию — по размерности (ASC), внутри размерности по цене (DESC).
 */
export const sortProducts = (products: ProductInterface[], sortType: SortType = DEFAULT_SORT): ProductInterface[] => {
    switch (sortType) {
        case 'articul':
            return [...products].sort((a, b) => (a.articul || '').localeCompare(b.articul || ''));

        case 'code':
            return [...products].sort((a, b) => (a.code || '').localeCompare(b.code || ''));

        case 'priceasc':
            return [...products].sort((a, b) => getPrice(a) - getPrice(b));

        case 'pricedesc':
            return [...products].sort((a, b) => getPrice(b) - getPrice(a));

        case 'sizes':
            // Внутри одной размерности — по цене от большего к меньшему
            return [...products].sort((a, b) => {
                const indexDiff = (a.sizes_index || NO_INDEX) - (b.sizes_index || NO_INDEX);
                if (indexDiff !== 0) return indexDiff;
                return getPrice(b) - getPrice(a);
            });

        case 'popularity':
        default:
            return [...products].sort((a, b) => {
                const aInStock = hasStock(a);
                const bInStock = hasStock(b);
                if (aInStock !== bInStock) return aInStock ? -1 : 1;
                return (a.popularity_index || NO_INDEX) - (b.popularity_index || NO_INDEX);
            });
    }
};
